// src/pages/admin/invoices/InvoiceDetails.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  Download,
  Description,
  AccessTime,
  Business,
  Person,
  Edit as EditIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import MainCard from 'components/MainCard';
import InvoiceStatusChip from 'components/invoices/InvoiceStatusChip';
import CommentSection from 'components/invoices/CommentSection';
import invoiceService from 'services/invoiceService';
import { formatCurrency, formatDate, formatDateTime } from 'utils/formatters';
import { useSnackbar } from 'hooks/useSnackbar';
import { useAsync } from 'hooks/useAsync';

const AdminInvoiceDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState('');
  const [rejectDialog, setRejectDialog] = useState({ open: false, comment: '' });
  const [approveDialog, setApproveDialog] = useState({ open: false, comment: '' });
  const [editBillingMonthDialog, setEditBillingMonthDialog] = useState({ open: false, billingMonth: null });
  const [editBillingUnitsDialog, setEditBillingUnitsDialog] = useState({
    open: false,
    timesheetId: null,
    billingUnits: '',
    billingUnitType: 'hours'
  });
  const { showSuccess, showError, snackbar, handleClose } = useSnackbar();

  const { loading, execute: fetchInvoice } = useAsync(async () => {
    const response = await invoiceService.getInvoice(id);
    setInvoice(response.invoice);
  });

  useEffect(() => {
    fetchInvoice().catch((err) => {
      console.error('Failed to fetch invoice:', err);
      setError('Failed to load invoice details');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleApprove = async () => {
    try {
      await invoiceService.approveInvoice(id, approveDialog.comment);
      setApproveDialog({ open: false, comment: '' });
      fetchInvoice();
    } catch (error) {
      console.error('Failed to approve invoice:', error);
      alert('Failed to approve invoice');
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.comment.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      await invoiceService.rejectInvoice(id, rejectDialog.comment);
      setRejectDialog({ open: false, comment: '' });
      fetchInvoice();
    } catch (error) {
      console.error('Failed to reject invoice:', error);
      alert('Failed to reject invoice');
    }
  };

  const handleDownload = async (type) => {
    try {
      const { blob, filename } =
        type === 'invoice' ? await invoiceService.downloadInvoiceFile(id) : await invoiceService.downloadTimesheetFile(id);

      invoiceService.downloadFile(blob, filename);
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download file');
    }
  };

  const handleDownloadIndividualTimesheet = async (timesheet) => {
    try {
      const { blob, filename } = await invoiceService.downloadIndividualTimesheetFile(id, timesheet.id);

      invoiceService.downloadFile(blob, filename);
    } catch (error) {
      console.error('Failed to download timesheet:', error);
      alert('Failed to download timesheet file');
    }
  };

  const handleOpenEditBillingMonth = () => {
    // Parse the current billing_month string to a Date object
    const currentMonth = invoice.billing_month ? parse(invoice.billing_month + '-01', 'yyyy-MM-dd', new Date()) : null;
    setEditBillingMonthDialog({ open: true, billingMonth: currentMonth });
  };

  const handleUpdateBillingMonth = async () => {
    try {
      const formattedMonth = editBillingMonthDialog.billingMonth ? format(editBillingMonthDialog.billingMonth, 'yyyy-MM') : null;

      await invoiceService.updateBillingMonth(id, formattedMonth);
      setEditBillingMonthDialog({ open: false, billingMonth: null });
      showSuccess('Billing month updated successfully');
      fetchInvoice(); // Refresh invoice data
    } catch (error) {
      console.error('Failed to update billing month:', error);
      showError('Failed to update billing month');
    }
  };

  const handleOpenEditBillingUnits = (timesheet) => {
    setEditBillingUnitsDialog({
      open: true,
      timesheetId: timesheet.id,
      billingUnits: timesheet.billing_units,
      billingUnitType: timesheet.billing_unit_type || 'hours'
    });
  };

  const handleOpenEditLegacyBillingUnits = () => {
    setEditBillingUnitsDialog({
      open: true,
      timesheetId: null, // null indicates legacy billing units
      billingUnits: invoice.billing_units || '',
      billingUnitType: invoice.billing_unit_type || 'hours'
    });
  };

  const handleUpdateBillingUnits = async () => {
    try {
      // If timesheetId is null, update legacy billing units on the invoice itself
      if (editBillingUnitsDialog.timesheetId === null) {
        await invoiceService.updateBillingHours(id, editBillingUnitsDialog.billingUnits, editBillingUnitsDialog.billingUnitType);
      } else {
        // Otherwise, update the specific timesheet
        await invoiceService.updateTimesheetBillingUnits(
          id,
          editBillingUnitsDialog.timesheetId,
          editBillingUnitsDialog.billingUnits,
          editBillingUnitsDialog.billingUnitType
        );
      }
      setEditBillingUnitsDialog({
        open: false,
        timesheetId: null,
        billingUnits: '',
        billingUnitType: 'hours'
      });
      showSuccess('Billing units updated successfully');
      fetchInvoice(); // Refresh invoice data
    } catch (error) {
      console.error('Failed to update billing units:', error);
      showError('Failed to update billing units');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return <Alert severity="error">{error || 'Invoice not found'}</Alert>;
  }

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center',
          mb: 3
        }}
      >
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Invoice {invoice.custom_invoice_number || invoice.invoice_number}
        </Typography>
        {invoice.status !== 'paid' && invoice.status !== 'approved' && invoice.status !== 'rejected' && (
          <>
            <Button variant="outlined" color="error" startIcon={<Cancel />} onClick={() => setRejectDialog({ open: true, comment: '' })}>
              Reject
            </Button>
          </>
        )}
        {invoice.status !== 'paid' && invoice.status !== 'approved' && (
          <>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => setApproveDialog({ open: true, comment: '' })}
            >
              Approve
            </Button>
          </>
        )}
      </Stack>

      <Grid container spacing={3}>
        {/* Invoice Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          <MainCard>
            <Stack spacing={3}>
              {/* Header */}
              <Box>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h5">Invoice Details</Typography>
                    {/* Warning indicator for missing timesheet */}
                    {(!invoice.timesheets || invoice.timesheets.length === 0) && !invoice.timesheet_file && (
                      <Tooltip title="Missing timesheet" arrow>
                        <WarningIcon color="warning" />
                      </Tooltip>
                    )}
                  </Stack>
                  <InvoiceStatusChip status={invoice.status} />
                </Stack>
              </Box>

              <Divider />

              {/* Parties Involved */}
              <Grid container spacing={3}>
                {/* Vendor Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ p: 2, bgcolor: 'primary.lighter' }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                        mb: 1
                      }}
                    >
                      <Business color="primary" />
                      <Typography variant="subtitle2" color="primary">
                        VENDOR
                      </Typography>
                    </Stack>
                    <Typography variant="h6">{invoice.vendor?.company_name || '-'}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {invoice.vendor?.user
                        ? [invoice.vendor?.user.first_name, invoice.vendor?.user.last_name].filter(Boolean).join(' ')
                        : '-'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {invoice.vendor?.user?.email || '-'}
                    </Typography>
                    {invoice.vendor?.phone && (
                      <Typography variant="body2" color="textSecondary">
                        {invoice.vendor.phone}
                      </Typography>
                    )}
                  </Card>
                </Grid>

                {/* Client Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ p: 2, bgcolor: 'success.lighter' }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                        mb: 1
                      }}
                    >
                      <Person color="success" />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'success.main'
                        }}
                      >
                        CLIENT
                      </Typography>
                    </Stack>
                    <Typography variant="h6">{invoice.client?.company_name || '-'}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {invoice.client?.user
                        ? [invoice.client?.user.first_name, invoice.client?.user.last_name].filter(Boolean).join(' ')
                        : '-'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {invoice.client?.user?.email || '-'}
                    </Typography>
                  </Card>
                </Grid>

                {/* Project Info */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="textSecondary">
                    Project
                  </Typography>
                  <Typography variant="h6">{invoice.project?.name || '-'}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {invoice.project?.description || ''}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider />
                </Grid>

                {/* Invoice Details */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="textSecondary">
                    Invoice Number
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500
                    }}
                  >
                    {invoice.custom_invoice_number || invoice.invoice_number || '-'}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="textSecondary">
                    Invoice Date
                  </Typography>
                  <Typography variant="body1">{formatDate(invoice.invoice_date)}</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="textSecondary">
                    Due Date
                  </Typography>
                  <Typography variant="body1">{invoice.due_date ? formatDate(invoice.due_date) : '-'}</Typography>
                  {invoice.due_date && new Date(invoice.due_date) < new Date() && invoice.status !== 'paid' && (
                    <Chip label="OVERDUE" color="error" size="small" sx={{ mt: 0.5 }} />
                  )}
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        Billing Month
                      </Typography>
                      <Typography variant="body1">
                        {invoice.billing_month
                          ? new Date(invoice.billing_month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
                          : '-'}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleOpenEditBillingMonth} color="primary">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Grid>

                {/* Show legacy billing units if available or if no timesheets exist */}
                {!invoice.timesheets?.length && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center'
                      }}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          Billing Hours/Units
                        </Typography>
                        <Typography variant="body1">
                          {invoice.billing_units
                            ? `${invoice.billing_units} ${invoice.billing_unit_type === 'hours' ? 'hours' : invoice.billing_unit_type || 'units'}`
                            : 'Not set'}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleOpenEditLegacyBillingUnits()} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1">{invoice.description || 'No description provided'}</Typography>
                </Grid>

                {/* Timesheets Section - New multi-timesheet structure */}
                {invoice.timesheets && invoice.timesheets.length > 0 && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Divider />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Timesheets
                      </Typography>
                      <Stack spacing={2}>
                        {invoice.timesheets.map((timesheet) => (
                          <Card key={timesheet.id} sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 600
                                  }}
                                >
                                  {timesheet.subcontractor
                                    ? `${timesheet.subcontractor.first_name} ${timesheet.subcontractor.last_name} (Subcontractor)`
                                    : `${invoice.vendor?.user?.first_name} ${invoice.vendor?.user?.last_name} (Vendor)`}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  sx={{
                                    alignItems: 'center'
                                  }}
                                >
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="caption" color="textSecondary">
                                      Billing Units
                                    </Typography>
                                    <Typography variant="body1">
                                      {timesheet.billing_units}{' '}
                                      {timesheet.billing_unit_type === 'hours' ? 'hours' : timesheet.billing_unit_type}
                                    </Typography>
                                  </Box>
                                  <IconButton size="small" onClick={() => handleOpenEditBillingUnits(timesheet)} color="primary">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Grid>
                              {timesheet.billing_period_start && timesheet.billing_period_end && (
                                <Grid size={{ xs: 12, sm: 4 }}>
                                  <Typography variant="caption" color="textSecondary">
                                    Period
                                  </Typography>
                                  <Typography variant="body1">
                                    {formatDate(timesheet.billing_period_start)} - {formatDate(timesheet.billing_period_end)}
                                  </Typography>
                                </Grid>
                              )}
                              {timesheet.notes && (
                                <Grid size={{ xs: 12 }}>
                                  <Typography variant="caption" color="textSecondary">
                                    Notes
                                  </Typography>
                                  <Typography variant="body2">{timesheet.notes}</Typography>
                                </Grid>
                              )}
                              {timesheet.timesheet_file && (
                                <Grid size={{ xs: 12 }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AccessTime />}
                                    endIcon={<Download />}
                                    onClick={() => handleDownloadIndividualTimesheet(timesheet)}
                                    sx={{ mt: 1 }}
                                  >
                                    Download Timesheet
                                  </Button>
                                </Grid>
                              )}
                            </Grid>
                          </Card>
                        ))}
                      </Stack>
                    </Grid>
                  </>
                )}

                <Grid size={{ xs: 12 }}>
                  <Divider />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h6">Total Amount</Typography>
                    <Typography variant="h4" color="primary">
                      {formatCurrency(invoice.amount, invoice.currency)}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              {/* Files */}
              {(invoice.invoice_file || invoice.timesheet_file) && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Attached Files
                    </Typography>
                    <Stack spacing={1}>
                      {invoice.invoice_file && (
                        <Button
                          variant="outlined"
                          startIcon={<Description />}
                          endIcon={<Download />}
                          onClick={() => handleDownload('invoice')}
                          fullWidth
                          sx={{ justifyContent: 'space-between' }}
                        >
                          Invoice Document
                        </Button>
                      )}
                      {invoice.timesheet_file && (
                        <Button
                          variant="outlined"
                          startIcon={<AccessTime />}
                          endIcon={<Download />}
                          onClick={() => handleDownload('timesheet')}
                          fullWidth
                          sx={{ justifyContent: 'space-between' }}
                        >
                          Timesheet
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </MainCard>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            {/* Timeline */}
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Timeline
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Created
                  </Typography>
                  <Typography variant="body2">{formatDateTime(invoice.created_at)}</Typography>
                </Box>

                {invoice.submitted_at && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'info.main'
                      }}
                    >
                      Submitted
                    </Typography>
                    <Typography variant="body2">{formatDateTime(invoice.submitted_at)}</Typography>
                  </Box>
                )}

                {invoice.approved_at && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'success.main'
                      }}
                    >
                      Approved
                    </Typography>
                    <Typography variant="body2">{formatDateTime(invoice.approved_at)}</Typography>
                    {invoice.approvedBy && (
                      <Typography variant="caption" color="textSecondary">
                        by {invoice.approvedBy?.name}
                      </Typography>
                    )}
                  </Box>
                )}

                {invoice.rejected_at && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'error.main'
                      }}
                    >
                      Rejected
                    </Typography>
                    <Typography variant="body2">{formatDateTime(invoice.rejected_at)}</Typography>
                    {invoice.rejectedBy && (
                      <Typography variant="caption" color="textSecondary">
                        by {invoice.rejectedBy?.name}
                      </Typography>
                    )}
                  </Box>
                )}

                {invoice.paid_at && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'primary.main'
                      }}
                    >
                      Paid
                    </Typography>
                    <Typography variant="body2">{formatDateTime(invoice.paid_at)}</Typography>
                  </Box>
                )}
              </Stack>
            </Card>

            {/* Quick Stats */}
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Quick Stats
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Invoice Age
                  </Typography>
                  <Typography variant="body2">
                    {Math.floor((new Date() - new Date(invoice.created_at)) / (1000 * 60 * 60 * 24))} days
                  </Typography>
                </Box>
                {invoice.due_date && invoice.status !== 'paid' && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Days Until Due
                    </Typography>
                    <Typography variant="body2">
                      {Math.ceil((new Date(invoice.due_date) - new Date()) / (1000 * 60 * 60 * 24))} days
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Comments
                  </Typography>
                  <Typography variant="body2">{invoice.comments?.length || 0}</Typography>
                </Box>
              </Stack>
            </Card>

            {/* Payment Details */}
            {invoice.payment_detail && (
              <Card sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Payment Details
                </Typography>
                <Stack spacing={1}>
                  {invoice.payment_detail.label && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Label
                      </Typography>
                      <Typography variant="body2">{invoice.payment_detail.label}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Account Holder
                    </Typography>
                    <Typography variant="body2">{invoice.payment_detail.account_holder_name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Bank Name
                    </Typography>
                    <Typography variant="body2">{invoice.payment_detail.bank_name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Account Number
                    </Typography>
                    <Typography variant="body2">{invoice.payment_detail.account_number}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      IBAN
                    </Typography>
                    <Typography variant="body2">{invoice.payment_detail.iban}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      SWIFT / BIC
                    </Typography>
                    <Typography variant="body2">{invoice.payment_detail.swift_code}</Typography>
                  </Box>
                  {invoice.payment_detail.routing_number && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Routing Number
                      </Typography>
                      <Typography variant="body2">{invoice.payment_detail.routing_number}</Typography>
                    </Box>
                  )}
                  {invoice.payment_detail.intermediary_bank_name && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Intermediary Bank
                      </Typography>
                      <Typography variant="body2">{invoice.payment_detail.intermediary_bank_name}</Typography>
                    </Box>
                  )}
                  {invoice.payment_detail.intermediary_swift_code && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Intermediary SWIFT
                      </Typography>
                      <Typography variant="body2">{invoice.payment_detail.intermediary_swift_code}</Typography>
                    </Box>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        </Grid>

        {/* Comments Section */}
        <Grid size={{ xs: 12 }}>
          <CommentSection invoiceId={id} />
        </Grid>
      </Grid>

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, comment: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Invoice (Admin Override)</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are approving this invoice as an administrator. This will override the normal client approval process.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Approve invoice <strong>{invoice.custom_invoice_number || invoice.invoice_number}</strong> for{' '}
            <strong>{formatCurrency(invoice.amount, invoice.currency)}</strong>?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comment (Optional)"
            value={approveDialog.comment}
            onChange={(e) => setApproveDialog({ ...approveDialog, comment: e.target.value })}
            placeholder="Add any comments about this approval..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, comment: '' })}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, comment: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Invoice (Admin Override)</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are rejecting this invoice as an administrator. The vendor will be notified.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting invoice <strong>{invoice.custom_invoice_number || invoice.invoice_number}</strong>:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason *"
            required
            value={rejectDialog.comment}
            onChange={(e) => setRejectDialog({ ...rejectDialog, comment: e.target.value })}
            placeholder="Explain why this invoice is being rejected..."
            error={rejectDialog.comment && !rejectDialog.comment.trim()}
            helperText="This will be sent to the vendor"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, comment: '' })}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={!rejectDialog.comment.trim()}>
            Reject Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Billing Month Dialog */}
      <Dialog
        open={editBillingMonthDialog.open}
        onClose={() => setEditBillingMonthDialog({ open: false, billingMonth: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Billing Month</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, mt: 1 }}>
            Update the billing month for invoice <strong>{invoice.custom_invoice_number || invoice.invoice_number}</strong>. Leave empty to
            auto-calculate based on invoice period or milestone due date.
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Billing Month"
              views={['year', 'month']}
              value={editBillingMonthDialog.billingMonth}
              onChange={(date) => setEditBillingMonthDialog({ ...editBillingMonthDialog, billingMonth: date })}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: 'Select the month this invoice should be grouped under'
                }
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBillingMonthDialog({ open: false, billingMonth: null })}>Cancel</Button>
          <Button onClick={handleUpdateBillingMonth} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Billing Units Dialog */}
      <Dialog
        open={editBillingUnitsDialog.open}
        onClose={() => setEditBillingUnitsDialog({ open: false, timesheetId: null, billingUnits: '', billingUnitType: 'hours' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Billing Units</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, mt: 1 }}>
            Update the billing units for this timesheet.
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              type="number"
              label="Billing Units"
              value={editBillingUnitsDialog.billingUnits}
              onChange={(e) => setEditBillingUnitsDialog({ ...editBillingUnitsDialog, billingUnits: e.target.value })}
              slotProps={{
                htmlInput: { min: 0, step: 0.01 }
              }}
              required
            />
            <TextField
              fullWidth
              select
              label="Unit Type"
              value={editBillingUnitsDialog.billingUnitType}
              onChange={(e) => setEditBillingUnitsDialog({ ...editBillingUnitsDialog, billingUnitType: e.target.value })}
              required
            >
              <MenuItem value="hours">Hours</MenuItem>
              <MenuItem value="days">Days</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBillingUnitsDialog({ open: false, timesheetId: null, billingUnits: '', billingUnitType: 'hours' })}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateBillingUnits}
            variant="contained"
            color="primary"
            disabled={!editBillingUnitsDialog.billingUnits || editBillingUnitsDialog.billingUnits <= 0}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminInvoiceDetails;
